import { useCallback } from 'react';
import { Alert, Button, Input, notification } from 'antd';
import { SaveOutlined, UndoOutlined } from '@ant-design/icons';
import ReactSplit from 'react-split';
import HeartLoader from '@/shared/ui/HeartLoader';
import {
  useGetWorksheetQuery,
  useUpdateWorksheetMutation,
} from '@/state/slices/reports';
import { useWorksheetEditorState } from '../hooks/useWorksheetEditorState';
import { useEditorResizableLayout } from '../hooks/useEditorResizableLayout';
import { WorksheetSidebar } from './WorksheetSidebar';
import { WorksheetCanvas } from './WorksheetCanvas';
import styles from './WorksheetEditor.module.css';
import './primitives/draggable.module.css';

interface WorksheetEditorProps {
  uuid: string;
}

export function WorksheetEditor({ uuid }: WorksheetEditorProps) {
  const { data, isLoading, error } = useGetWorksheetQuery(uuid);
  const [updateWorksheet, { isLoading: isSaving }] =
    useUpdateWorksheetMutation();

  const { splitProps } = useEditorResizableLayout();

  const {
    worksheet,
    unsavedChanges,
    updateName,
    updateSections,
    updateContextGroups,
    updateMeasurementGroups,
    updateFindingGroups,
    updateFlagGroups,
    updateFollowUpGroups,
    getPayload,
    resetToServer,
  } = useWorksheetEditorState(data);

  const handleSave = useCallback(async () => {
    const payload = getPayload();
    if (!payload) return;
    try {
      await updateWorksheet(payload).unwrap();
      notification.success({ title: 'Worksheet saved' });
    } catch {
      notification.error({ title: 'Failed to save worksheet' });
    }
  }, [getPayload, updateWorksheet]);

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <HeartLoader showText />
        </div>
      </div>
    );
  }

  if (error || !worksheet) {
    return (
      <div className={styles.container}>
        <Alert type="error" title="Failed to load worksheet" showIcon />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Input
          value={worksheet.name}
          onChange={(e) => updateName(e.target.value)}
          className={styles.nameInput}
          size="large"
        />
        <div className={styles.actions}>
          <Button
            icon={<UndoOutlined />}
            onClick={resetToServer}
            disabled={!unsavedChanges}
          >
            Discard
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={isSaving}
            disabled={!unsavedChanges}
          >
            Save
          </Button>
        </div>
      </div>

      <ReactSplit {...splitProps} className={styles.split}>
        <div className={styles.sidebarColumn}>
          <WorksheetSidebar
            worksheet={worksheet}
            onSectionsChange={updateSections}
            onContextGroupsChange={updateContextGroups}
            onMeasurementGroupsChange={updateMeasurementGroups}
            onFindingGroupsChange={updateFindingGroups}
            onFlagGroupsChange={updateFlagGroups}
            onFollowUpGroupsChange={updateFollowUpGroups}
          />
        </div>
        <div className={styles.canvasColumn}>
          <WorksheetCanvas
            worksheet={worksheet}
            onContextGroupsChange={updateContextGroups}
            onMeasurementGroupsChange={updateMeasurementGroups}
            onFindingGroupsChange={updateFindingGroups}
            onFlagGroupsChange={updateFlagGroups}
            onFollowUpGroupsChange={updateFollowUpGroups}
          />
        </div>
      </ReactSplit>
    </div>
  );
}
